/**
 * Reading a code somebody has just scanned, pasted or typed.
 *
 * ## Why this is shared rather than written twice
 *
 * The thirty-character alphabet is `ondo.mint_work_code()`'s and
 * `ondo.mint_share_code()`'s, and the normalising rule is
 * `ondo.normalise_work_code()`'s. **Nothing enforces that this file agrees with
 * them** — it is the same arrangement as every other file in this directory, and
 * the comment is the enforcement. If the alphabet changes in SQL it changes
 * here.
 *
 * It lives here because the phone and the web both have to read a code off a
 * user, and they have to reach the same verdict. A code a collector types into
 * the app and the same code pasted into the website must be the same code, and
 * two copies of the parsing is how that stops being true.
 *
 * ## What a reader is given, in practice
 *
 * Rarely the bare code. A camera hands over whatever was in the symbol, which
 * may be a URL from a QR code on a label. A person pastes with the spaces and
 * the line break the PDF gave them, or types it in lower case with no hyphens.
 * All of those are the same code and are accepted as one — which is the whole
 * reason `ondo.normalise_work_code()` exists, stated there as "a code read off a
 * label and a code copied out of the app must be the same code".
 *
 * ## The two kinds are told apart by LENGTH, not by the S
 *
 * A work code normalises to `OND` + 8 (eleven characters); a share code to
 * `ONDS` + 8 (twelve). So an eleven-character `ONDS…` is a WORK code whose first
 * payload character happens to be S — `S` is in the alphabet — and only a
 * twelve-character one is a share code. Reading the prefix alone would
 * misclassify one work code in thirty.
 */
/**
 * The alphabet both minting functions draw from.
 *
 * Thirty characters: no 0/O, no 1/I/L, no U. The first two pairs are what
 * somebody transcribing a code from a photograph confuses; U is out because
 * removing it removes most of the accidental words a random generator produces.
 */
export const CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
const PAYLOAD = 8;
/**
 * A code as it compares — `ondo.normalise_work_code()`, in TypeScript.
 *
 * Upper case, letters and digits only. Note it strips letters it does not
 * understand rather than rejecting them, exactly as the SQL does, so the
 * validity question is asked afterwards and in one place.
 */
export function normaliseCode(code) {
    return code.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
}
/** Every payload character is in the alphabet. */
function payloadIsValid(payload) {
    return (payload.length === PAYLOAD &&
        [...payload].every((character) => CODE_ALPHABET.includes(character)));
}
/**
 * Read whatever a user or a camera supplied, or null.
 *
 * **Null is a real answer**, and every failure returns it: an empty string, a
 * sentence, a URL to somewhere else, a code of the wrong length, a code
 * containing a letter the alphabet excludes. A caller that wants to say WHY
 * cannot — and deliberately: this is the same door
 * `ondo.share_codes`'s redeem is, where "no such code" and "a code that cannot
 * exist" must not be distinguishable, or the answer confirms which codes exist.
 *
 * A URL is handled by looking at its last path-like segment rather than by
 * matching a host, because the host differs between production, a preview
 * deployment and a local machine, and a reader that only trusted one of them
 * would fail in testing in a way nobody could explain.
 */
export function readCode(input) {
    const candidates = [input, ...segmentsOf(input)];
    for (const candidate of candidates) {
        const body = normaliseCode(candidate);
        if (body.length === 3 + PAYLOAD && body.startsWith("OND")) {
            const payload = body.slice(3);
            if (payloadIsValid(payload)) {
                return { kind: "work", code: formatWorkCode(payload), payload };
            }
        }
        if (body.length === 4 + PAYLOAD && body.startsWith("ONDS")) {
            const payload = body.slice(4);
            if (payloadIsValid(payload)) {
                return { kind: "share", code: formatShareCode(payload), payload };
            }
        }
    }
    return null;
}
/**
 * The parts of a URL or deep link worth trying, longest-looking first.
 *
 * Splits on everything that separates one thing from another in a link — the
 * scheme, the slashes, the query, the fragment — and hands back the pieces. A
 * QR code on a label may hold `https://ondo.art/works/OND-K7PQ-3M2X`, a share
 * link may hold `?code=OND-S-…`, and a deep link may hold `ondo://share/OND-S-…`;
 * all three reduce to a segment this can read.
 */
function segmentsOf(input) {
    return input
        .split(/[\s/?&#=:]+/)
        .filter((part) => part.length >= 3 + PAYLOAD && part.length <= 20);
}
/** `OND-K7PQ-3M2X` from eight payload characters. */
export function formatWorkCode(payload) {
    return `OND-${payload.slice(0, 4)}-${payload.slice(4)}`;
}
/** `OND-S-K7PQ-3M2X` from eight payload characters. The S keeps it apart. */
export function formatShareCode(payload) {
    return `OND-S-${payload.slice(0, 4)}-${payload.slice(4)}`;
}
/**
 * Two codes are the same code.
 *
 * The comparison `ondo.normalise_work_code()` makes, so a code held in a record
 * and a code read off a label match without either being reformatted first.
 */
export function sameCode(a, b) {
    const left = normaliseCode(a);
    return left !== "" && left === normaliseCode(b);
}
//# sourceMappingURL=codes.js.map